import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserPlus, faSave, faEraser, faTrash, faEdit, faUsers,
  faUserCircle, faCaretDown, faUser, faSyncAlt, faSignOutAlt,
  faCrown, faCheckCircle, faExclamationCircle, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import '../scss/UserManagement.scss';
import BaseURL from '../assets/contants/BaseURL';

const UserManagement = () => {
  const navigate = useNavigate();
  
  // State variables
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editUserDetailId, setEditUserDetailId] = useState(null);
  const [editUserId, setEditUserId] = useState(null);
  const [organizationEmail, setOrganizationEmail] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobile_no: '',
    designation: '',
    user_active: 'true'
  });
  const [enablePasswordEdit, setEnablePasswordEdit] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ class: '', width: '0%' });

  const getAuthToken = () => sessionStorage.getItem('Token');
  const getCSRFToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') return value;
    }
    return '';
  };

  const getOrganizationEmail = () => sessionStorage.getItem('organization_email');
  const getCurrentUserEmail = () => sessionStorage.getItem('email');
  const getIsSuperuser = () => sessionStorage.getItem('is_superuser') === 'true';

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const checkAuth = () => {
    const token = getAuthToken();
    if (!token) {
      showNotification('Please log in first', 'error');
      setTimeout(() => navigate('/login'), 2000);
      return false;
    }
    setOrganizationEmail(getOrganizationEmail());
    setCurrentUserEmail(getCurrentUserEmail());
    setIsSuperuser(getIsSuperuser());
    return true;
  };

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const handleUnauthorized = () => {
    sessionStorage.clear();
    showNotification('Session expired. Please log in again.', 'error');
    setTimeout(() => navigate('/login'), 2000);
  };

  const loadUsers = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      handleUnauthorized();
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BaseURL}Userauth/userdetail/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'Authorization': `Token ${token}`
        },
        credentials: 'same-origin'
      });

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.message !== 'Unauthorized access') {
        showNotification('Error loading users', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const isOrganizationAdmin = (userEmail) => {
    if (!organizationEmail || !userEmail) return false;
    return userEmail.toLowerCase() === organizationEmail.toLowerCase();
  };

  const isCurrentUser = (userEmail) => {
    if (!currentUserEmail || !userEmail) return false;
    return userEmail.toLowerCase() === currentUserEmail.toLowerCase();
  };

  const shouldShowDeleteButton = (userEmail) => {
    if (isSuperuser) {
      return !isCurrentUser(userEmail);
    }
    if (isOrganizationAdmin(userEmail)) return false;
    if (isCurrentUser(userEmail)) return false;
    return true;
  };

  const checkPasswordStrength = (password) => {
    if (!password || password.length === 0) {
      setPasswordStrength({ class: '', width: '0%' });
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

    if (strength <= 2) {
      setPasswordStrength({ class: 'strength-weak', width: '33.33%' });
    } else if (strength <= 4) {
      setPasswordStrength({ class: 'strength-medium', width: '66.66%' });
    } else {
      setPasswordStrength({ class: 'strength-strong', width: '100%' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  const handlePasswordEditToggle = (e) => {
    const checked = e.target.checked;
    setEnablePasswordEdit(checked);
    if (!checked) {
      setFormData(prev => ({ ...prev, password: '' }));
      setPasswordStrength({ class: '', width: '0%' });
    }
  };

  const editUser = (user) => {
    setFormData({
      name: user.usermod?.first_name || '',
      email: user.usermod?.email || '',
      password: '',
      mobile_no: user.mobile_no || '',
      designation: user.designation || '',
      user_active: user.userActive ? 'true' : 'false'
    });
    setEditUserDetailId(user.userdetail_id);
    setEditUserId(user.usermod?.id);
    setEnablePasswordEdit(false);
    setPasswordStrength({ class: '', width: '0%' });
    
    // Scroll to form
    document.querySelector('.user-form-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const deleteUser = async (userDetailId, userEmail) => {
    if (!shouldShowDeleteButton(userEmail)) {
      showNotification('You cannot delete this user', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) return;
    if (!checkAuth()) return;

    const token = getAuthToken();
    try {
      const response = await fetch(`${BaseURL}Userauth/userdetail/${userDetailId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'Authorization': `Token ${token}`
        },
        credentials: 'same-origin'
      });

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) throw new Error('Failed to delete user');

      showNotification('User deleted successfully', 'success');
      loadUsers();
      if (editUserDetailId === userDetailId) clearForm();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Error deleting user', 'error');
    }
  };

  const clearForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      mobile_no: '',
      designation: '',
      user_active: 'true'
    });
    setEditUserDetailId(null);
    setEditUserId(null);
    setEnablePasswordEdit(false);
    setPasswordStrength({ class: '', width: '0%' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkAuth()) return;

    const { name, email, password, mobile_no, designation, user_active } = formData;
    
    if (!email || !name) {
      showNotification('Name and Email are required', 'error');
      return;
    }

    if (!editUserDetailId && !password) {
      showNotification('Password is required for new users', 'error');
      return;
    }

    const token = getAuthToken();
    let includePassword = false;
    
    if (editUserDetailId && enablePasswordEdit) {
      if (!password) {
        showNotification('Please enter a new password or uncheck the password edit option', 'error');
        return;
      }
      includePassword = true;
    }

    if (editUserDetailId) {
      const updateData = {
        userdetail_id: editUserDetailId,
        user_id: editUserId,
        usermod: {
          first_name: name,
          email: email
        },
        mobile_no: mobile_no,
        designation: designation,
        userActive: user_active === 'true'
      };
      if (includePassword) updateData.password = password;

      try {
        const response = await fetch(`${BaseURL}Userauth/userdetail/${editUserDetailId}/`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify(updateData),
          credentials: 'same-origin'
        });

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.message || err.error || 'Failed to update user');
        }

        showNotification('User updated successfully', 'success');
        loadUsers();
        clearForm();
      } catch (error) {
        console.error('Error updating user:', error);
        showNotification(error.message || 'Error updating user', 'error');
      }
    } else {
      const formDataObj = {
        email: email,
        name: name,
        password: password,
        mobile_no: mobile_no,
        designation: designation,
        user_active: user_active === 'true'
      };

      try {
        const response = await fetch(`${BaseURL}Userauth/web-create/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'Authorization': `Token ${token}`
          },
          body: JSON.stringify(formDataObj),
          credentials: 'same-origin'
        });

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.message || err.error || 'Failed to create user');
        }

        showNotification('User created successfully', 'success');
        loadUsers();
        clearForm();
      } catch (error) {
        console.error('Error creating user:', error);
        showNotification(error.message || 'Error creating user', 'error');
      }
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;
    
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${BaseURL}Userauth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
            'Authorization': `Token ${token}`
          },
          credentials: 'same-origin'
        });
      } catch (error) {
        console.log('Logout API error:', error);
      }
    }
    
    sessionStorage.clear();
    navigate('/login');
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        (user.usermod?.first_name && user.usermod.first_name.toLowerCase().includes(term)) ||
        (user.usermod?.email && user.usermod.email.toLowerCase().includes(term)) ||
        (user.designation && user.designation.toLowerCase().includes(term))
      );
      setFilteredUsers(filtered);
    }
  };

  useEffect(() => {
    if (checkAuth()) {
      loadUsers();
    }
  }, [loadUsers]);

  const isEditMode = !!editUserDetailId;

  return (
    <div className="user-management-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                                  <img
                                    src={orgLogo}
                                    alt="Organization Logo"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #4361ee'
                                    }}
                                    onError={() => setOrgLogo(null)}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                              <div className="account-menu show">
                                <div className="account-menu-item plan-info">
                                  <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                  <span>Basic Plan</span>
                                </div>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faUser} /> Profile
                                </button>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                                </button>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                  <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                                </button>
                              </div>
                            )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>User Management</h1>
          <p>Manage all system users and their permissions</p>
        </div>

        {/* User Form */}
        <div className="user-form-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faUserPlus} /> {isEditMode ? 'Edit User' : 'Add User'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  placeholder="e.g., John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email Address <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  placeholder="e.g., john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder={isEditMode && !enablePasswordEdit ? 'Password editing disabled' : 'Enter password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isEditMode && !enablePasswordEdit}
                />
                {formData.password && (
                  <div className="password-strength">
                    <div className={`password-strength-bar ${passwordStrength.class}`} style={{ width: passwordStrength.width }}></div>
                  </div>
                )}
                {isEditMode && (
                  <div className="enable-password-edit">
                    <input
                      type="checkbox"
                      id="enablePasswordEdit"
                      checked={enablePasswordEdit}
                      onChange={handlePasswordEditToggle}
                    />
                    <label htmlFor="enablePasswordEdit">Enable password editing</label>
                  </div>
                )}
                <small>Min. 8 characters with letters and numbers</small>
              </div>
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="text"
                  name="mobile_no"
                  placeholder="e.g., +1234567890"
                  value={formData.mobile_no}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Designation/Role</label>
                <input
                  type="text"
                  name="designation"
                  placeholder="e.g., Admin, Manager, Operator"
                  value={formData.designation}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Account Status</label>
                <select name="user_active" className="filter-select"  value={formData.user_active} onChange={handleInputChange}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} /> {isEditMode ? 'Update User' : 'Add User'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearForm}>
                <FontAwesomeIcon icon={faEraser} /> Clear
              </button>
              {isEditMode && (
                <button type="button" className="btn btn-danger" onClick={() => deleteUser(editUserDetailId, formData.email)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete User
                </button>
              )}
            </div>
          </form>
        </div>

        {/* User Table */}
        <div className="user-table-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faUsers} /> User List
          </h2>
          <div className="table-controls">
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="loading-spinner-container">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: 'var(--primary-color)' }} />
              <p>Loading users...</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Designation</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, index) => {
                      const userEmail = user.usermod?.email || '';
                      const isAdmin = isOrganizationAdmin(userEmail);
                      const showDelete = shouldShowDeleteButton(userEmail);
                      
                      return (
                        <tr key={user.userdetail_id}>
                          <td>{index + 1}</td>
                          <td>
                            {user.usermod?.first_name || '-'}
                            {isAdmin && <span className="org-admin-badge">Admin</span>}
                          </td>
                          <td>{user.usermod?.email || '-'}</td>
                          <td>{user.mobile_no || '-'}</td>
                          <td>{user.designation || '-'}</td>
                          <td>
                            <span className={`status-badge ${user.userActive ? 'status-active' : 'status-inactive'}`}>
                              {user.userActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="action-btns">
                            <button className="edit-btn" onClick={() => editUser(user)}>
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button 
                              className={`delete-btn ${!showDelete ? 'disabled' : ''}`}
                              onClick={() => showDelete && deleteUser(user.userdetail_id, userEmail)}
                              disabled={!showDelete}
                              title={!showDelete ? "Cannot delete organization admin or yourself" : ""}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default UserManagement;